import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import CompanySelector from './CompanySelector';
import Instructions from './Instructions';
import Metrics from './Metrics';
import PreferencesMonitoring from './PreferencesMonitoring';
import CompanyInfo from './CompanyInfo';
import { isMobilePhone } from '../utils/deviceDetection';
import ChatWidget from './ChatWidget';
import { isAdmin } from '../config/accessControl';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { trackVisit } from '../services/analytics';
import Conversations from './Conversations';
import Appointments from './Appointments';
import NewCall from './NewCall';
import Examinations from './Examinations';

const Controls = ({ userEmail, ...props }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [activeTab, setActiveTab] = useState('instructions');
  const [demoScenario, setDemoScenario] = useState('');
  const [error, setError] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const chatContainerRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const navigate = useNavigate();
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userCurrentCompany, setUserCurrentCompany] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  console.log('Controls mounted with userEmail:', userEmail);

  const fetchCompanies = useCallback(async () => {
    console.log("Fetching companies from:", `${API_BASE_URL}/companies`);
    try {
      const response = await fetch(`${API_BASE_URL}/companies`, {
        headers: {
          'X-Access-Token': process.env.REACT_APP_CONNECTOR_API_KEY
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log("Fetched companies:", data);
      setCompanies(data);
      setError(null);
    } catch (e) {
      console.error("Failed to fetch companies:", e);
      setError(`Failed to load companies. Error: ${e.message}`);
    }
  }, [API_BASE_URL]);

  const fetchDemoScenario = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/demo-scenario`, {
        headers: {
          'X-Access-Token': process.env.REACT_APP_CONNECTOR_API_KEY
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setDemoScenario(data.demo_scenario);
    } catch (e) {
      console.error("Failed to fetch demo scenario:", e);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchCompanies();
    fetchDemoScenario();
  }, [fetchCompanies, fetchDemoScenario]);

  const updateDemoScenario = async (companyName) => {
    try {
      // First update in main database
      const response = await fetch(`${API_BASE_URL}/demo-scenario`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Access-Token': process.env.REACT_APP_CONNECTOR_API_KEY
        },
        body: JSON.stringify({ company_name: companyName })
      });

      if (response.ok) {
        // Then trigger immediate context update
        const updateResponse = await fetch(`${process.env.REACT_APP_AI_ENDPOINT_URL}/update-company`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ company_name: companyName })
        });

        if (updateResponse.ok) {
          setDemoScenario(companyName);
        } else {
          throw new Error(`Failed to update context: ${updateResponse.statusText}`);
        }
      } else {
        throw new Error(`Failed to update demo scenario: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error updating demo scenario:', error);
    }
  };

  const addNewScenario = async (scenarioName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/add-scenario`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Access-Token': process.env.REACT_APP_CONNECTOR_API_KEY,
          'user-email': userEmail
        },
        body: JSON.stringify({ scenario_name: scenarioName })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log("New scenario added:", data);
        setCompanies(data.companies);
        setSelectedCompany(scenarioName);
        
        if (data.gdoc_url) {
          alert(`Scenario created successfully! Google Doc created at: ${data.gdoc_url}`);
        }
      } else {
        throw new Error(`Failed to add new scenario: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error adding new scenario:', error);
    }
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    autoResizeTextarea(e.target);
  };

  const autoResizeTextarea = (element) => {
    element.style.height = 'auto';
    element.style.height = (element.scrollHeight) + 'px';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    setIsMobile(isMobilePhone());
  }, []);

  const sendMessage = async () => {
    if (!inputMessage.trim() || !userEmail) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setChatMessages(prevMessages => [...prevMessages, { sender: 'You', message: userMessage }]);
    scrollToBottom();

    try {
      const API_URL = `${process.env.REACT_APP_AI_ENDPOINT_URL}/chat/stream`;

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          user_id: userEmail
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantResponse = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log("Stream complete");
          break;
        }

        const chunk = decoder.decode(value);
        assistantResponse += chunk;
        updateAssistantResponse(assistantResponse);
      }

      if (!assistantResponse) {
        console.log("No response content received");
        updateAssistantResponse("I'm having trouble responding. Can you try again?");
      }

    } catch (error) {
      console.error('Error:', error);
      updateAssistantResponse("Oops, I'm having trouble thinking right now. Can you try again?");
    }
  };

  const updateAssistantResponse = (response) => {
    setChatMessages(prevMessages => {
      const newMessages = [...prevMessages];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && lastMessage.sender === 'Mednavigator AI') {
        lastMessage.message = response;
      } else {
        newMessages.push({ sender: 'Mednavigator AI', message: response });
      }
      return newMessages;
    });
    scrollToBottom();
  };

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      resizeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: chatContainer.offsetWidth,
        height: chatContainer.offsetHeight
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      const newWidth = resizeStartRef.current.width + e.clientX - resizeStartRef.current.x;
      const newHeight = resizeStartRef.current.height + e.clientY - resizeStartRef.current.y;
      chatContainer.style.width = `${Math.max(300, Math.min(800, newWidth))}px`;
      chatContainer.style.height = `${Math.max(300, Math.min(800, newHeight))}px`;
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('mousedown', handleMouseDown);
    }
    return () => {
      if (chatContainer) {
        chatContainer.removeEventListener('mousedown', handleMouseDown);
      }
    };
  }, [handleMouseDown]);

  const scrollToBottom = () => {
    setTimeout(() => {
      const chatMessages = document.getElementById('chat-messages');
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight;
      }
    }, 0);
  };

  useEffect(() => {
    if (isChatOpen) {
      scrollToBottom();
    }
  }, [chatMessages, isChatOpen]);

  const deleteCompany = async (companyName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/delete-scenario`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Access-Token': process.env.REACT_APP_CONNECTOR_API_KEY
        },
        body: JSON.stringify({ company_name: companyName })
      });
      if (response.ok) {
        setCompanies(companies.filter(company => company !== companyName));
        if (selectedCompany === companyName) {
          setSelectedCompany('');
        }
        if (demoScenario === companyName) {
          setDemoScenario('');
        }
      } else {
        throw new Error(`Failed to delete scenario: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting company:', error);
    }
  };

  const startVoiceRecording = async () => {
    try {
      console.log("Requesting microphone access...");
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        } 
      });
      console.log("Microphone access granted");
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      const ws = new WebSocket('ws://localhost:8008/ws/voice-assistant/');
      
      ws.onopen = () => {
        console.log('Connected to voice assistant');
        mediaRecorder.start(250);
      };
      
      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
          try {
            const arrayBuffer = await event.data.arrayBuffer();
            const audioContext = new (window.AudioContext || window.webkitAudioContext)({
              sampleRate: 16000
            });
            
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            const channelData = audioBuffer.getChannelData(0);
            
            // Send chunks of 512 samples
            const chunkSize = 512;
            for (let i = 0; i < channelData.length; i += chunkSize) {
              const chunk = channelData.slice(i, i + chunkSize);
              if (chunk.length === chunkSize && ws.readyState === WebSocket.OPEN) {
                ws.send(chunk.buffer);
              }
            }
          } catch (error) {
            console.error('Error processing audio:', error);
          }
        }
      };
      
      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'transcription_update') {
            console.log('Transcription:', data.text);
            if (data.text && data.text !== "Processing...") {
              setChatMessages(prev => [...prev, {
                sender: 'You',
                message: data.text,
                isTranscription: true
              }]);
            }
          }
        } catch (error) {
          // If it's not JSON, assume it's audio data
          console.log('Received audio response');
          const audioBlob = new Blob([event.data], { type: 'audio/wav' });
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          await audio.play();
          URL.revokeObjectURL(audioUrl);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      ws.onclose = () => {
        console.log('Disconnected from voice assistant');
        mediaRecorder.stop();
        stream.getTracks().forEach(track => track.stop());
      };
  
      return { ws, mediaRecorder, stream };
    } catch (error) {
      console.error('Error starting voice recording:', error);
      setIsVoiceMode(false);
    }
  };
  
  // Replace the existing toggleVoiceMode function
  const toggleVoiceMode = () => {
    console.log('Toggle voice mode clicked. Current state:', isVoiceMode);
    setIsVoiceMode(prev => {
      console.log('Setting voice mode to:', !prev);
      if (!prev) {
        // Starting voice mode
        startVoiceRecording().then(({ ws, mediaRecorder, stream }) => {
          console.log('Voice recording started successfully');
          // Store references for cleanup when toggling off
          window.voiceAssistant = { ws, mediaRecorder, stream };
        }).catch(error => {
          console.error('Failed to start voice recording:', error);
          return false; // Don't toggle if there's an error
        });
      } else {
        // Stopping voice mode
        console.log('Stopping voice mode');
        if (window.voiceAssistant) {
          window.voiceAssistant.ws.close();
          window.voiceAssistant.mediaRecorder.stop();
          window.voiceAssistant.stream.getTracks().forEach(track => track.stop());
          delete window.voiceAssistant;
        }
      }
      return !prev;
    });
  };

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (userEmail) {
        const adminStatus = await isAdmin(userEmail);
        setIsAdminUser(adminStatus);
      }
    };
    checkAdminStatus();
  }, [userEmail]);

  const handleShowInstructions = async () => {
    if (!isAdminUser) {
      alert('Only administrators have access to system instructions.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/instructions/${selectedCompany}`, {
        // ... existing fetch logic
      });
      // ... rest of the code
    } catch (error) {
      console.error('Error fetching instructions:', error);
    }
  };

  const refreshContext = async () => {
    setIsRefreshing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_AI_ENDPOINT_URL}/update-company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company_name: demoScenario })
      });
      
      if (!response.ok) {
        throw new Error('Failed to refresh context');
      }
    } catch (error) {
      console.error('Error refreshing context:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const permanentlyDeleteCompany = async (companyName) => {
    if (window.confirm(`Are you sure you want to PERMANENTLY delete ${companyName}? This cannot be undone!`)) {
        try {
            const response = await fetch(`${API_BASE_URL}/permanently-delete-scenario`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Access-Token': process.env.REACT_APP_CONNECTOR_API_KEY
                },
                body: JSON.stringify({ company_name: companyName })
            });
            if (response.ok) {
                setCompanies(companies.filter(company => company !== companyName));
                if (selectedCompany === companyName) {
                    setSelectedCompany('');
                }
                if (demoScenario === companyName) {
                    setDemoScenario('');
                }
            } else {
                throw new Error(`Failed to permanently delete scenario: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error permanently deleting company:', error);
        }
    }
  };

  const updateUserSelectedCompany = async (companyName) => {
    if (!userEmail) return;
    
    try {
        const userRef = doc(db, 'userAccess', userEmail);
        await updateDoc(userRef, {
            currentScenario: companyName,
            lastUpdated: new Date()
        });
        
        // Also update context in the backend
        await fetch(`${process.env.REACT_APP_AI_ENDPOINT_URL}/update-company`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'User-Email': userEmail
            },
            body: JSON.stringify({ 
                company_name: companyName, 
                user_id: userEmail 
            })
        });
    } catch (error) {
        console.error('Error updating user selected company:', error);
    }
  };

  const handleSelectCompany = async (company) => {
    console.log(`Setting selected company to: ${company}`);
    setSelectedCompany(company);
    setUserCurrentCompany(company);
    await updateUserSelectedCompany(company);
    await trackVisit(company);
  };

  // Add this function to set a default company for the user
  const setDefaultCompany = useCallback(async () => {
    // Don't do anything if a company is already selected
    if (selectedCompany) return;
    
    // Only proceed if companies are loaded
    if (companies.length === 0) return;
    
    try {
      const userRef = doc(db, 'userAccess', userEmail);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const allowedScenarios = userData.scenarios;
        
        let defaultCompany = null;
        
        if (Array.isArray(allowedScenarios) && allowedScenarios.length > 0) {
          // For partners, use the first allowed scenario
          defaultCompany = allowedScenarios[0];
        } else if (allowedScenarios === 'all') {
          // For admins, use the first available company
          defaultCompany = companies[0];
        }
        
        if (defaultCompany) {
          setSelectedCompany(defaultCompany);
          setUserCurrentCompany(defaultCompany);
          await updateUserSelectedCompany(defaultCompany);
        }
      }
    } catch (error) {
      console.error("Error setting default company:", error);
    }
  }, [companies, selectedCompany, userEmail, updateUserSelectedCompany]);

  // Call this function when companies are loaded
  useEffect(() => {
    if (companies.length > 0 && !selectedCompany) {
      setDefaultCompany();
    }
  }, [companies, selectedCompany, setDefaultCompany]);

  // Auto-switch to New Call tab when postsurgery_followups is selected
  useEffect(() => {
    if (selectedCompany === 'postsurgery_followups') {
      setActiveTab('newCall');

      // Auto-scroll to the tab area on mobile devices
      setTimeout(() => {
        const tabsContainer = document.querySelector('nav[aria-label="Tabs"]');
        if (tabsContainer && isMobile) {
          // Scroll the tabs container into view
          tabsContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Also scroll within the horizontal tab container to show New Call button
          const newCallButton = Array.from(tabsContainer.querySelectorAll('button'))
            .find(btn => btn.textContent.trim() === 'New Call');

          if (newCallButton) {
            // Ensure the New Call button is visible in the horizontal scroll
            newCallButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          }
        }
      }, 100); // Small delay to ensure DOM updates are complete
    }
  }, [selectedCompany, isMobile]);

  // Fetch user's current company on component mount
  useEffect(() => {
    const fetchUserCompany = async () => {
      if (!userEmail) return;
      
      try {
        const userRef = doc(db, 'userAccess', userEmail);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const currentScenario = userData.currentScenario;
          
          if (currentScenario) {
            setUserCurrentCompany(currentScenario);
            setSelectedCompany(currentScenario);
          }
        }
      } catch (error) {
        console.error('Error fetching user company:', error);
      }
    };
    
    fetchUserCompany();
  }, [userEmail]);

  if (error) {
    return (
      <div className="p-4">
        <h1>Something went wrong</h1>
        <pre>{error.toString()}</pre>
        <button 
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
        >
          Reload page
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <img src="/images/pafos_logo.png" alt="PAfoS.AI Logo" className="h-11 w-auto" />
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <Link to="/" className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium">
                  Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-3xl font-bold">Control Panel</h1>
            </div>
            
            <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <CompanySelector
                  companies={companies}
                  selectedCompany={selectedCompany}
                  onSelectCompany={handleSelectCompany}
                  demoScenario={demoScenario}
                  onUpdateDemoScenario={updateDemoScenario}
                  onAddNewScenario={addNewScenario}
                  addButtonText="Add new scenario"
                  onDeleteCompany={deleteCompany}
                  permanentlyDeleteCompany={permanentlyDeleteCompany}
                  userEmail={userEmail}
                  userCurrentCompany={userCurrentCompany}
                />
              </div>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="border-b border-gray-200 overflow-x-auto no-scrollbar">
                  <nav className="-mb-px flex space-x-4 md:space-x-8 min-w-min" aria-label="Tabs">
                    {isAdminUser && (
                      <button
                        className={`inline-flex items-center px-2 md:px-1 pt-1 border-b-2 text-xs md:text-sm font-medium whitespace-nowrap ${
                          activeTab === 'instructions'
                            ? 'border-blue-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                        onClick={() => setActiveTab('instructions')}
                      >
                        Instructions
                      </button>
                    )}
                    <button
                      className={`inline-flex items-center px-2 md:px-1 pt-1 border-b-2 text-xs md:text-sm font-medium whitespace-nowrap ${
                        activeTab === 'metrics'
                          ? 'border-blue-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => setActiveTab('metrics')}
                    >
                      Metrics
                    </button>
                    <button
                      onClick={() => setActiveTab('preferencesMonitoring')}
                      className={`${
                        activeTab === 'preferencesMonitoring'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm`}
                    >
                      Preferences
                    </button>
                    <button
                      onClick={() => setActiveTab('conversations')}
                      className={`${
                        activeTab === 'conversations'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm`}
                    >
                      Conversations
                    </button>
                    <button
                      onClick={() => setActiveTab('appointments')}
                      className={`${
                        activeTab === 'appointments'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm`}
                    >
                      Appointments
                    </button>
                    {selectedCompany === 'uki_clinic_almaty' && (
                      <button
                        onClick={() => setActiveTab('examinations')}
                        className={`${
                          activeTab === 'examinations'
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm`}
                      >
                        Examinations
                      </button>
                    )}
                    {selectedCompany === 'postsurgery_followups' && (
                      <button
                        onClick={() => setActiveTab('newCall')}
                        className={`${
                          activeTab === 'newCall'
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm`}
                      >
                        New Call
                      </button>
                    )}
                  </nav>
                </div>
                <div className="mt-6">
                  {activeTab === 'instructions' && isAdminUser && (
                    <Instructions
                      selectedCompany={selectedCompany}
                      apiBaseUrl={API_BASE_URL}
                      apiKey={process.env.REACT_APP_CONNECTOR_API_KEY}
                      userEmail={userEmail}
                    />
                  )}
                  {activeTab === 'metrics' && (
                    <Metrics
                      apiBaseUrl={API_BASE_URL}
                      apiKey={process.env.REACT_APP_CONNECTOR_API_KEY}
                      selectedCompany={selectedCompany}
                    />
                  )}
                  {activeTab === 'preferencesMonitoring' && (
                    <PreferencesMonitoring
                      apiBaseUrl={API_BASE_URL}
                      apiKey={process.env.REACT_APP_CONNECTOR_API_KEY}
                    />
                  )}
                  {activeTab === 'conversations' && (
                    <Conversations 
                      selectedCompany={selectedCompany}
                      apiBaseUrl={API_BASE_URL}
                      apiKey={process.env.REACT_APP_CONNECTOR_API_KEY}
                      userEmail={userEmail}
                    />
                  )}
                  {activeTab === 'appointments' && (
                    <Appointments
                      selectedCompany={selectedCompany}
                      apiBaseUrl={API_BASE_URL}
                      apiKey={process.env.REACT_APP_CONNECTOR_API_KEY}
                      userEmail={userEmail}
                    />
                  )}
                  {activeTab === 'examinations' && selectedCompany === 'uki_clinic_almaty' && (
                    <Examinations
                      selectedCompany={selectedCompany}
                      userEmail={userEmail}
                    />
                  )}
                  {activeTab === 'newCall' && selectedCompany === 'postsurgery_followups' && (
                    <NewCall
                      selectedCompany={selectedCompany}
                    />
                  )}
                </div>
              </div>
            </div>

            {selectedCompany && (
              <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <CompanyInfo
                    selectedCompany={selectedCompany}
                    apiBaseUrl={API_BASE_URL}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <ChatWidget 
        userEmail={userEmail} 
        selectedScenario={selectedCompany}
      />

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap');

          #chat-widget {
            position: fixed;
            bottom: 20px;
            right: 20px;
            font-family: 'Roboto', sans-serif;
            z-index: 1000;
          }

          #chat-toggle {
            padding: 10px 20px;
            background: linear-gradient(145deg, #4CAF50, #36A166);
            color: white;
            border: none;
            cursor: pointer;
            border-radius: 25px;
            font-size: 16px;
            display: flex;
            align-items: center;
            gap: 10px;
            transition: background 0.3s ease, box-shadow 0.3s ease;
          }

          #chat-toggle:hover {
            background: #36A166;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          }

          #chat-container {
            width: ${isMobile ? '100%' : '600px'};
            height: ${isMobile ? '80vh' : '515px'};
            min-width: ${isMobile ? '100%' : '375px'};
            min-height: ${isMobile ? '80vh' : '300px'};
            max-width: ${isMobile ? '100%' : '1000px'};
            max-height: ${isMobile ? '80vh' : '640px'};
            border: 1px solid #ccc;
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(10px);
            display: flex;
            flex-direction: column;
            margin-top: ${isMobile ? '0' : '10px'};
            border-radius: ${isMobile ? '0' : '20px'};
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            resize: ${isMobile ? 'none' : 'both'};
            position: relative;
          }

          #chat-messages {
            flex: 1;
            overflow-y: auto;
            padding: 20px;
            display: flex;
            flex-direction: column;
            scrollbar-width: none;  /* Firefox */
            -ms-overflow-style: none;  /* Internet Explorer 10+ */
          }

          #chat-messages::-webkit-scrollbar {
            width: 0;
            height: 0;
            display: none;
          }

          #chat-messages > div {
            white-space: pre-wrap;
            margin-top: 10px;
          }

          #chat-input-area {
            display: flex;
            padding: 10px;
            border-top: 1px solid #ccc;
            background: #f9f9f9;
          }

          #chat-input {
            flex-grow: 1;
            padding: 10px;
            border: 1px solid #ccc;
            border-radius: 20px;
            font-size: 14px;
            resize: none;
            overflow-y: hidden;
            line-height: 1.5;
            max-height: 100px;
          }

          #chat-send {
            margin-left: 10px;
            padding: 10px;
            background-color: #36A166;
            color: white;
            border: none;
            cursor: pointer;
            border-radius: ${isMobile ? '20px' : '50%'};
            font-size: 16px;
            transition: background 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            width: ${isMobile ? 'auto' : '40px'};
            height: ${isMobile ? 'auto' : '40px'};
          }

          #chat-send:hover {
            background-color: #4CAF50;
          }

          .chat-bubble {
            max-width: 80%;
            padding: 12px 16px;
            border-radius: 20px;
            margin-bottom: 10px;
            word-wrap: break-word;
            font-size: 14px;
            line-height: 1.5;
          }

          .user-message {
            align-self: flex-end;
            background: linear-gradient(145deg, #DCF8C6, #B5EAD8);
            color: #333;
          }

          .assistant-message {
            align-self: flex-start;
            background-color: #E0E0E0;
            color: #333;
          }

          #resize-handle {
            display: ${isMobile ? 'none' : 'block'};
            position: absolute;
            right: 0;
            bottom: 0;
            width: 20px;
            height: 20px;
            cursor: se-resize;
            background: linear-gradient(135deg, transparent 50%, #4CAF50 50%);
          }

          ${isMobile ? `
            #chat-widget {
              bottom: 0;
              right: 0;
              left: 0;
            }

            #chat-toggle {
              width: 100%;
              border-radius: 0;
              justify-content: center;
            }
          ` : ''}
        `}
    </style>
    </div>
  );
};

export default Controls;
